/**
 * WhatsApp Order Utility
 *
 * Generates beautifully formatted WhatsApp order messages for products and cart items.
 * Features modern, mobile-optimized message formatting with:
 * - Virtual bank account details for direct payment
 * - Direct checkout links
 * - Clean, professional layout
 */

/**
 * Format phone number for WhatsApp API
 * Removes spaces, dashes, and ensures proper format
 * @param {string} phone - Phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove all non-numeric characters except +
  let formatted = phone.replace(/[^\d+]/g, '');

  // If starts with 0, assume Nigeria and add country code
  if (formatted.startsWith('0')) {
    formatted = '234' + formatted.substring(1);
  }

  // If starts with +, remove it (WhatsApp API doesn't need it)
  if (formatted.startsWith('+')) {
    formatted = formatted.substring(1);
  }

  return formatted;
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: NGN)
 * @returns {string} - Formatted amount
 */
const formatCurrency = (amount, currency = 'NGN') => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Generate WhatsApp order link for a single product
 * Creates a beautifully formatted, modern message with payment options
 * @param {Object} params - Order parameters
 * @param {Object} params.product - Product details
 * @param {number} params.quantity - Quantity to order
 * @param {Object} params.selectedVariations - Selected product variations
 * @param {string} params.whatsappNumber - Merchant's WhatsApp number
 * @param {string} params.storeName - Store name
 * @param {string} params.currency - Currency code
 * @param {Object} params.virtualAccount - Merchant's virtual account {accountNumber, accountName, bankName}
 * @param {string} params.checkoutUrl - Direct checkout URL
 * @returns {string} - WhatsApp URL
 */
export const generateProductOrderLink = ({
  product,
  quantity = 1,
  selectedVariations = {},
  whatsappNumber,
  storeName,
  currency = 'NGN',
  virtualAccount = null,
  checkoutUrl = ''
}) => {
  if (!whatsappNumber || !product) return null;

  const phoneNumber = formatPhoneNumber(whatsappNumber);
  if (!phoneNumber) return null;

  // Calculate total
  const price = product.price || 0;
  const total = price * quantity;

  // Build sleek, modern message
  let message = '';

  // Clean header
  message += `Hi *${storeName}*\n\n`;
  message += `I'd like to place an order:\n\n`;

  // Product details with clean formatting
  message += `*ORDER DETAILS*\n\n`;
  message += `*${product.name}*\n`;

  // Variations
  if (Object.keys(selectedVariations).length > 0) {
    Object.entries(selectedVariations).forEach(([type, value]) => {
      message += `${type}: ${value}\n`;
    });
  }

  message += `\n`;
  message += `Qty: ${quantity}\n`;
  message += `Price: ${formatCurrency(price, currency)}\n\n`;

  // Total
  message += `*TOTAL: ${formatCurrency(total, currency)}*\n\n`;

  // Payment section - Paystack Terminal / legacy DVA fallback
  if (virtualAccount && virtualAccount.accountNumber) {
    message += `*PAYMENT OPTIONS*\n\n`;
    message += `*${virtualAccount.isLegacyDva ? 'Legacy Bank Transfer' : 'Paystack Terminal Transfer'}:*\n`;
    message += `Bank: ${virtualAccount.bankName}\n`;
    message += `Account: ${virtualAccount.accountNumber}\n`;
    message += `Name: ${virtualAccount.accountName}\n\n`;
  }

  // Direct checkout link
  if (checkoutUrl) {
    message += `*Or pay online:*\n`;
    message += `${checkoutUrl}\n\n`;
  }

  // Footer
  message += `_Please confirm availability._`;

  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);

  return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
};

/**
 * Generate WhatsApp order link for cart items
 * Creates a beautifully formatted order summary with payment options
 * @param {Object} params - Order parameters
 * @param {Array} params.items - Cart items array [{product, qty}]
 * @param {number} params.total - Cart total
 * @param {string} params.whatsappNumber - Merchant's WhatsApp number
 * @param {string} params.storeName - Store name
 * @param {string} params.currency - Currency code
 * @param {Object} params.virtualAccount - Merchant's virtual account
 * @param {string} params.checkoutUrl - Direct checkout URL
 * @returns {string} - WhatsApp URL
 */
export const generateCartOrderLink = ({
  items,
  total,
  whatsappNumber,
  storeName,
  currency = 'NGN',
  virtualAccount = null,
  checkoutUrl = ''
}) => {
  if (!whatsappNumber || !items || items.length === 0) return null;

  const phoneNumber = formatPhoneNumber(whatsappNumber);
  if (!phoneNumber) return null;

  // Build sleek message
  let message = '';

  // Header
  message += `Hi *${storeName}*\n\n`;
  message += `I'd like to order:\n\n`;

  // Order details
  message += `*ORDER DETAILS*\n\n`;

  items.forEach((item, index) => {
    const { product, qty } = item;
    const itemTotal = (product.price || 0) * qty;

    message += `${index + 1}. *${product.name}*\n`;
    if (product.variationText) {
      message += `   ${product.variationText}\n`;
    }
    message += `   ${qty} × ${formatCurrency(product.price, currency)}\n`;
    message += `   = ${formatCurrency(itemTotal, currency)}\n\n`;
  });

  // Total
  message += `*TOTAL: ${formatCurrency(total, currency)}*\n\n`;

  // Payment section
  if (virtualAccount && virtualAccount.accountNumber) {
    message += `*PAYMENT OPTIONS*\n\n`;
    message += `*${virtualAccount.isLegacyDva ? 'Legacy Bank Transfer' : 'Paystack Terminal Transfer'}:*\n`;
    message += `Bank: ${virtualAccount.bankName}\n`;
    message += `Account: ${virtualAccount.accountNumber}\n`;
    message += `Name: ${virtualAccount.accountName}\n\n`;
  }

  // Direct checkout link
  if (checkoutUrl) {
    message += `*Or pay online:*\n`;
    message += `${checkoutUrl}\n\n`;
  }

  // Footer
  message += `_Please confirm availability._`;

  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);

  return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
};

/**
 * Generate a merchant reply template for order confirmation
 * This is the message template merchants can use to respond to customers
 * @param {Object} params - Order parameters
 * @param {string} params.customerName - Customer name
 * @param {string} params.orderSummary - Brief order summary
 * @param {number} params.total - Order total
 * @param {Object} params.virtualAccount - Merchant's virtual account
 * @param {string} params.currency - Currency code
 * @returns {string} - Formatted message template
 */
export const generateMerchantReplyTemplate = ({
  customerName = 'Customer',
  orderSummary,
  total,
  virtualAccount = null,
  currency = 'NGN'
}) => {
  let message = '';

  message += `Hi ${customerName}!\n\n`;
  message += `Thanks for your order. Here are the details:\n\n`;

  if (orderSummary) {
    message += `*Order:* ${orderSummary}\n`;
  }
  message += `*Total:* ${formatCurrency(total, currency)}\n\n`;

  // Payment instructions
  if (virtualAccount && virtualAccount.accountNumber) {
    message += `*HOW TO PAY*\n\n`;
    message += `Transfer to:\n`;
    message += `*${virtualAccount.bankName}*\n`;
    message += `*${virtualAccount.accountNumber}*\n`;
    message += `${virtualAccount.accountName}\n\n`;
    message += `Amount: *${formatCurrency(total, currency)}*\n\n`;
  }

  message += `Send payment confirmation and we'll process your order right away!\n\n`;
  message += `_Thank you for shopping with us._`;

  return message;
};

/**
 * Open WhatsApp order in new tab/app
 * @param {string} url - WhatsApp URL
 */
export const openWhatsAppOrder = (url) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Check if WhatsApp orders are enabled for a store
 * @param {Object} store - Store object
 * @returns {boolean} - Whether WhatsApp orders are enabled
 */
export const isWhatsAppOrdersEnabled = (store) => {
  return store?.whatsappOrdersEnabled && store?.whatsappNumber;
};

/**
 * Get payment rail info from store object. Prefer Paystack Terminal and keep
 * legacy DVA only as fallback.
 * @param {Object} store - Store object
 * @returns {Object|null} - Virtual account details or null
 */
export const getVirtualAccountFromStore = (store) => {
  if (store?.virtualTerminalAccountNumber) {
    return {
      accountNumber: store.virtualTerminalAccountNumber,
      accountName: store.virtualTerminalAccountName || store.name,
      bankName: store.virtualTerminalBank || 'Paystack',
      terminalCode: store.paystackVirtualTerminalCode,
      isLegacyDva: false
    };
  }

  if (!store?.virtualAccountNumber) return null;

  return {
    accountNumber: store.virtualAccountNumber,
    accountName: store.virtualAccountName,
    bankName: store.virtualAccountBank,
    isLegacyDva: true
  };
};

/**
 * Helper to get custom domain from store object
 * Handles both customDomain (string) and customDomains (array) formats
 * @param {Object} store - Store object
 * @returns {string|null} - Custom domain or null
 */
const getCustomDomain = (store) => {
  // First check for direct customDomain field (from backend API responses)
  if (store?.customDomain) {
    return store.customDomain;
  }
  // Then check for customDomains array (from Prisma include)
  if (store?.customDomains && Array.isArray(store.customDomains) && store.customDomains.length > 0) {
    return store.customDomains[0]?.domain || null;
  }
  return null;
};

/**
 * Generate the correct checkout URL for a store
 * Uses custom domain if available, otherwise uses subdomain format
 * @param {Object} params - Parameters
 * @param {Object} params.store - Store object with slug and customDomain/customDomains
 * @param {string} params.productId - Product ID for single product checkout
 * @param {number} params.quantity - Quantity (optional, default 1)
 * @param {string} params.platformDomain - Platform domain (e.g., 'kwikahost.com')
 * @returns {string} - Full checkout URL
 */
export const generateCheckoutUrl = ({
  store,
  productId,
  quantity = 1,
  platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com'
}) => {
  if (!store?.slug) return '';

  // Get custom domain using helper that handles both formats
  const customDomain = getCustomDomain(store);

  // Determine base URL: custom domain or subdomain
  let baseUrl;
  if (customDomain) {
    // Use custom domain (full domain, e.g., mystore.kwikahost.com)
    baseUrl = customDomain.startsWith('http')
      ? customDomain
      : `https://${customDomain}`;
  } else {
    // Use subdomain format: {storeSlug}.{platformDomain}
    baseUrl = `https://${store.slug}.${platformDomain}`;
  }

  // Build checkout URL
  const checkoutPath = '/checkout';
  const params = new URLSearchParams();

  if (productId) {
    params.append('product', productId);
  }
  if (quantity > 1) {
    params.append('qty', quantity.toString());
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}${checkoutPath}?${queryString}` : `${baseUrl}${checkoutPath}`;
};

/**
 * Generate cart checkout URL for a store
 * @param {Object} params - Parameters
 * @param {Object} params.store - Store object with slug and customDomain/customDomains
 * @param {string} params.platformDomain - Platform domain (e.g., 'kwikahost.com')
 * @returns {string} - Full checkout URL
 */
export const generateCartCheckoutUrl = ({
  store,
  platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com'
}) => {
  if (!store?.slug) return '';

  // Get custom domain using helper that handles both formats
  const customDomain = getCustomDomain(store);

  // Determine base URL: custom domain or subdomain
  let baseUrl;
  if (customDomain) {
    baseUrl = customDomain.startsWith('http')
      ? customDomain
      : `https://${customDomain}`;
  } else {
    baseUrl = `https://${store.slug}.${platformDomain}`;
  }

  return `${baseUrl}/checkout`;
};

/**
 * Create a WhatsApp order via API and generate a link with order URL
 * This creates a tracked order in the system that both merchant and customer can view/edit
 * @param {Object} params - Order parameters
 * @param {Object} params.product - Product details {id, name, price, images}
 * @param {number} params.quantity - Quantity to order
 * @param {Object} params.selectedVariations - Selected product variations
 * @param {Object} params.store - Store object {slug, name, whatsappNumber, customDomain}
 * @param {Object} params.customerInfo - Customer info {name, phone, email, address}
 * @param {string} params.currency - Currency code
 * @param {Object} params.virtualAccount - Virtual account details
 * @returns {Promise<{success: boolean, whatsappUrl: string, orderUrl: string, order: Object}>}
 */
export const createProductWhatsAppOrder = async ({
  product,
  quantity = 1,
  selectedVariations = {},
  store,
  customerInfo = {},
  currency = 'NGN',
  virtualAccount = null
}) => {
  if (!store?.slug || !store?.whatsappNumber || !product) {
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    // Import axios dynamically to avoid circular dependencies
    const axios = (await import('../axiosConfig')).default;

    // Create the order via API
    const response = await axios.post('/whatsapp-orders/create', {
      storeSlug: store.slug,
      items: [{
        productId: product.id,
        quantity,
        variations: selectedVariations,
        // Unit-based selling fields
        unitOfMeasure: product.unitOfMeasure || null,
        unitQuantity: product.sellByUnit ? quantity : null,
        pricePerUnit: product.sellByUnit ? product.price : null
      }],
      customerInfo,
      currency
    });

    if (!response.data.success) {
      return { success: false, error: response.data.error };
    }

    const { order, orderUrl } = response.data;

    // Generate WhatsApp message with order link
    const phoneNumber = formatPhoneNumber(store.whatsappNumber);
    const price = product.price || 0;
    const total = price * quantity;

    let message = '';
    message += `Hi *${store.name}*\n\n`;
    message += `I'd like to place an order:\n\n`;
    message += `*ORDER #${order.orderNumber}*\n\n`;
    message += `*${product.name}*\n`;

    if (Object.keys(selectedVariations).length > 0) {
      Object.entries(selectedVariations).forEach(([type, value]) => {
        message += `${type}: ${value}\n`;
      });
    }

    message += `\nQty: ${quantity}\n`;
    message += `Price: ${formatCurrency(price, currency)}\n\n`;
    message += `*TOTAL: ${formatCurrency(total, currency)}*\n\n`;

    // Add order management link
    message += `*VIEW/MANAGE ORDER:*\n`;
    message += `${orderUrl}\n\n`;

    // Payment section
    if (virtualAccount && virtualAccount.accountNumber) {
      message += `*PAYMENT OPTIONS*\n\n`;
      message += `*${virtualAccount.isLegacyDva ? 'Legacy Bank Transfer' : 'Paystack Terminal Transfer'}:*\n`;
      message += `Bank: ${virtualAccount.bankName}\n`;
      message += `Account: ${virtualAccount.accountNumber}\n`;
      message += `Name: ${virtualAccount.accountName}\n\n`;
    }

    message += `_Please confirm availability._`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    return {
      success: true,
      whatsappUrl,
      orderUrl,
      order
    };
  } catch (error) {
    console.error('[WhatsApp Order] Create error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to create order'
    };
  }
};

/**
 * Create a WhatsApp order for cart items via API
 * @param {Object} params - Order parameters
 * @param {Array} params.items - Cart items [{product: {id, name, price}, qty}]
 * @param {number} params.total - Cart total
 * @param {Object} params.store - Store object
 * @param {Object} params.customerInfo - Customer info
 * @param {string} params.currency - Currency code
 * @param {Object} params.virtualAccount - Virtual account details
 * @returns {Promise<{success: boolean, whatsappUrl: string, orderUrl: string, order: Object}>}
 */
export const createCartWhatsAppOrder = async ({
  items,
  total,
  store,
  customerInfo = {},
  currency = 'NGN',
  virtualAccount = null
}) => {
  if (!store?.slug || !store?.whatsappNumber || !items || items.length === 0) {
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    const axios = (await import('../axiosConfig')).default;

    // Create the order via API
    const response = await axios.post('/whatsapp-orders/create', {
      storeSlug: store.slug,
      items: items.map(item => ({
        productId: item.product.id,
        quantity: item.qty,
        variations: item.product.selectedVariations || item.variations || {},
        // Unit-based selling fields
        unitOfMeasure: item.product.unitOfMeasure || item.unitOfMeasure || null,
        unitQuantity: item.unitQuantity || (item.product.sellByUnit ? item.qty : null),
        pricePerUnit: item.pricePerUnit || (item.product.sellByUnit ? item.product.price : null)
      })),
      customerInfo,
      currency
    });

    if (!response.data.success) {
      return { success: false, error: response.data.error };
    }

    const { order, orderUrl } = response.data;

    // Generate WhatsApp message with order link
    const phoneNumber = formatPhoneNumber(store.whatsappNumber);

    let message = '';
    message += `Hi *${store.name}*\n\n`;
    message += `I'd like to order:\n\n`;
    message += `*ORDER #${order.orderNumber}*\n\n`;

    items.forEach((item, index) => {
      const { product, qty } = item;
      const itemTotal = (product.price || 0) * qty;
      message += `${index + 1}. *${product.name}*\n`;
      if (product.variationText) {
        message += `   ${product.variationText}\n`;
      }
      message += `   ${qty} × ${formatCurrency(product.price, currency)}\n`;
      message += `   = ${formatCurrency(itemTotal, currency)}\n\n`;
    });

    message += `*TOTAL: ${formatCurrency(total, currency)}*\n\n`;

    // Add order management link
    message += `*VIEW/MANAGE ORDER:*\n`;
    message += `${orderUrl}\n\n`;

    // Payment section
    if (virtualAccount && virtualAccount.accountNumber) {
      message += `*PAYMENT OPTIONS*\n\n`;
      message += `*${virtualAccount.isLegacyDva ? 'Legacy Bank Transfer' : 'Paystack Terminal Transfer'}:*\n`;
      message += `Bank: ${virtualAccount.bankName}\n`;
      message += `Account: ${virtualAccount.accountNumber}\n`;
      message += `Name: ${virtualAccount.accountName}\n\n`;
    }

    message += `_Please confirm availability._`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    return {
      success: true,
      whatsappUrl,
      orderUrl,
      order
    };
  } catch (error) {
    console.error('[WhatsApp Order] Create cart error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to create order'
    };
  }
};

/**
 * Generate WhatsApp order URL from store info
 * @param {Object} params - Parameters
 * @param {Object} params.store - Store object with slug and customDomain/customDomains
 * @param {string} params.whatsappOrderId - The unique WhatsApp order ID
 * @param {string} params.platformDomain - Platform domain
 * @returns {string} - Full order URL
 */
export const generateWhatsAppOrderUrl = ({
  store,
  whatsappOrderId,
  platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com'
}) => {
  if (!store?.slug || !whatsappOrderId) return '';

  // Get custom domain using helper that handles both formats
  const customDomain = getCustomDomain(store);

  let baseUrl;
  if (customDomain) {
    baseUrl = customDomain.startsWith('http')
      ? customDomain
      : `https://${customDomain}`;
  } else {
    baseUrl = `https://${store.slug}.${platformDomain}`;
  }

  return `${baseUrl}/wa/${whatsappOrderId}`;
};

export default {
  formatPhoneNumber,
  generateProductOrderLink,
  generateCartOrderLink,
  generateMerchantReplyTemplate,
  openWhatsAppOrder,
  isWhatsAppOrdersEnabled,
  getVirtualAccountFromStore,
  generateCheckoutUrl,
  generateCartCheckoutUrl,
  createProductWhatsAppOrder,
  createCartWhatsAppOrder,
  generateWhatsAppOrderUrl
};
